import type { ReactNode } from 'react';

/* Les briques de statistique, partagées.
 *
 * Elles sont nées dans l'écran Statistiques, pour ses deux onglets. La
 * synthèse de séance pose exactement les mêmes questions — un taux, un
 * histogramme, une jauge par club — et les posait avec ses propres formes :
 * cartes grises, titres courants, pourcentages à point. Deux vocabulaires pour
 * un même sujet, à deux écrans d'écart.
 *
 * Elles sortent donc du fichier qui les a vues naître. Rien n'y change : ce
 * sont les mêmes, au même endroit du texte, avec les mêmes raisons écrites. */

/** Un pourcentage écrit en français : virgule décimale, espace insécable.
 *
 *  « 58.8% » voisinait « 17,3 putts » sur l'onglet d'à côté : deux notations
 *  du même nombre à un onglet d'écart. L'espace est insécable pour que le
 *  signe ne se retrouve jamais seul en début de ligne sur un téléphone. */
export const pct = (n: number): string =>
    `${n.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}\u00a0%`;

/* Une carte de statistique : papier blanc, titre en petites capitales avec son
 * icône, et une note en bas.
 *
 * Le même habillage que les étapes de l'écran de jeu et les blocs de l'écran
 * de départ. Trois familles de cartes différentes dans la même application, ce
 * sont trois choses à apprendre pour lire la même sorte d'information. */
export function CarteStat({ icone, titre, note, children }: {
    icone: ReactNode; titre: string; note?: string; children: ReactNode;
}) {
    return (
        <section className="rounded-box border border-base-300 bg-base-100 p-3 shadow-sm">
            <h2 className="mb-3 flex items-center gap-1.5 px-0.5 text-[11px] font-bold uppercase tracking-[0.14em]"
                style={{ color: 'var(--tuile-encre)' }}>
                <span style={{ color: 'var(--color-primary)' }}>{icone}</span>
                {titre}
            </h2>
            {children}
            {note && <p className="mt-2 px-0.5 text-xs opacity-50">{note}</p>}
        </section>
    );
}

/* Un grand chiffre, sa queue en petit.
 *
 * « +7,4 » se lit d'abord « +7 » : c'est l'entier qui porte le sens, la
 * décimale nuance. Les écrire à la même taille oblige l'œil à trier, et à trois
 * chiffres côte à côte cela fait trois tris pour une seule lecture.
 *
 * La coupe se fait au premier caractère qui n'est plus un chiffre, et non sur
 * la virgule : la queue n'est pas toujours une décimale. « 5 min » s'affichait
 * entièrement en corps 30, l'unité aussi grosse que la durée. */
export function GrandChiffre({ valeur, mot, ton }: {
    valeur: string | null; mot: string; ton?: string;
}) {
    const brut = valeur ?? '—';
    const i = brut.search(/[^0-9+−\-\s\u00a0\u202f]/);
    const tete = i <= 0 ? brut : brut.slice(0, i);
    const queue = i <= 0 ? '' : brut.slice(i);
    return (
        <div>
            <div className="font-score text-3xl leading-none" style={ton ? { color: ton } : undefined}>
                {tete}
                {queue && <span className="text-xl opacity-70">{queue}</span>}
            </div>
            <div className="mt-1 text-[9px] font-bold uppercase tracking-[0.12em] opacity-55">{mot}</div>
        </div>
    );
}

/** Une colonne d'histogramme : ce qui s'écrit dessus, sa hauteur, sa teinte. */
export interface Colonne {
    cle: string;
    /** Sous la colonne, en bas de casse comme sur la carte de score. */
    mot: string;
    /** Au-dessus de la colonne. */
    valeur: string;
    /** De 0 à 1. */
    part: number;
    ton: string;
    /** Le même chiffre à la période précédente, de 0 à 1, en repère pointé. */
    avant?: number | null;
}

/* L'histogramme de l'écran, écrit une fois.
 *
 * La répartition des scores et les fautes répondent à la même question — « de
 * quoi est fait le total » — et se dessinaient de deux façons : colonnes d'un
 * côté, barres horizontales de l'autre. Une seule forme, apprise une fois.
 *
 * Le compte va AU-DESSUS de la colonne. En dessous il tombait contre
 * l'étiquette, et l'œil devait séparer un nombre d'un mot collés. */
export function Colonnes({ colonnes, hauteur = '4.5rem' }: { colonnes: Colonne[]; hauteur?: string }) {
    return (
        <div className="flex items-stretch gap-2" style={{ height: hauteur }}>
            {colonnes.map((c) => (
                <div key={c.cle} className="flex min-w-0 flex-1 flex-col items-center gap-1">
                    <span className="font-score text-sm leading-none">{c.valeur}</span>
                    <span className="relative flex w-full flex-1 flex-col justify-end">
                        {/* Zéro ne dessine RIEN. Un talon de six pour cent
                            donnait une barre là où il n'y a plus rien — et sur
                            cette carte, une faute tombée à zéro est justement
                            la bonne nouvelle à lire. Le repère pointé reste,
                            lui, et montre d'où l'on vient. */}
                        {c.part > 0 && (
                            <span className="w-full rounded-t"
                                style={{ height: `${Math.max(6, c.part * 100)}%`, background: c.ton }} />
                        )}
                        {c.avant != null && (
                            <span className="absolute inset-x-0 border-t-2 border-dotted"
                                style={{
                                    bottom: `${Math.min(100, c.avant * 100)}%`,
                                    borderColor: 'var(--tuile-encre)',
                                    opacity: 0.55,
                                }} />
                        )}
                    </span>
                    <span className="w-full truncate text-center text-[9px] font-bold tracking-wide opacity-55">
                        {c.mot}
                    </span>
                </div>
            ))}
        </div>
    );
}

/* La jauge de fiabilité, partagée par les deux onglets.
 *
 * Elle existait en double, et pas de la même couleur : vert / orange / rouge
 * d'un côté, vert / laiton / brique de l'autre, avec des seuils différents. Sur
 * le même écran, à un onglet d'écart, le même club changeait de couleur sans
 * avoir changé de valeur.
 *
 * Les seuils : sept balles sur dix, c'est un club sur lequel on bâtit un coup ;
 * six, un club qui passe mais qu'on ne choisit pas pour sauver un trou ; en
 * dessous, un club à travailler.
 *
 * La piste est un ASSOMBRISSEMENT de ce qu'il y a dessous, pas une couleur.
 * Peinte en sable, elle disparaissait dès que la ligne du club était elle-même
 * sur sable : la barre flottait sans échelle, et 40 % ressemblait à 100 %.
 *
 * Le laiton d'ÉCRITURE pour la bande du milieu, pas celui de fond. Mesuré sur
 * les pixels rendus, le laiton pâle donne 1,57:1 contre le sable de la piste :
 * en plein soleil, une jauge à 65 % n'aurait affiché aucune barre. Le laiton
 * sombre monte à environ 5:1 sans quitter la famille. */
export function JaugeFiabilite({ taux, part }: { taux: number; part?: number }) {
    return (
        <span className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-full"
            style={{ background: 'color-mix(in oklab, var(--tuile-encre) 16%, transparent)' }}>
            <span className="block h-full rounded-full"
                style={{
                    width: `${Math.max(3, part ?? taux)}%`,
                    background: taux >= 70 ? 'var(--color-primary)'
                        : taux >= 60 ? 'var(--brass-ink)'
                            : 'var(--brique-bord)',
                }} />
        </span>
    );
}
